import { useState, useEffect, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Copy, Check, Ban, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const STATUS_LABELS = {
  pending: { t: "Attivo", c: "bg-accent/15 text-accent" },
  accepted: { t: "Usato", c: "bg-primary/10 text-primary" },
  expired: { t: "Scaduto", c: "bg-muted text-muted-foreground" },
  revoked: { t: "Revocato", c: "bg-destructive/10 text-destructive" },
};

const inviteLink = (token) => `${window.location.origin}/accept-invite?token=${token}`;

export const InviteManager = () => {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get("/auth/invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!email.trim()) return toast.error("Inserisci un'email.");
    setCreating(true);
    try {
      const r = await api.post("/auth/invites", { email: email.trim(), name: name.trim() });
      const link = inviteLink(r.data.token);
      setLastLink(link);
      setCopied(false);
      setEmail(""); setName("");
      toast.success("Invito creato");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCreating(false); }
  };

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error("Copia non riuscita. Seleziona e copia manualmente."); }
  };

  const revoke = async (id) => {
    try { await api.post(`/auth/invites/${id}/revoke`); toast.success("Invito revocato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/auth/invites/${id}`); toast.success("Invito eliminato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="invite-manager">
      <p className="text-xs text-muted-foreground">
        Genera un invito monouso per un nuovo professionista beta. Copia il link e invialo manualmente.
        Ogni invito crea un'organizzazione separata e vuota.
      </p>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div className="space-y-1">
          <Label>Email invitato</Label>
          <Input data-testid="invite-new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@esempio.it" />
        </div>
        <div className="space-y-1">
          <Label>Nome / centro (opz.)</Label>
          <Input data-testid="invite-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Centro Rossi" />
        </div>
        <Button data-testid="invite-create-btn" onClick={create} disabled={creating} className="rounded-full">
          <UserPlus className="h-4 w-4 mr-1" />Genera invito
        </Button>
      </div>

      {lastLink && (
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-3 space-y-2" data-testid="invite-last-link">
          <Label className="text-xs">Link d'invito (copialo ora)</Label>
          <div className="flex gap-2">
            <Input readOnly value={lastLink} data-testid="invite-link-value" className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button size="sm" variant="outline" data-testid="invite-copy-btn" onClick={() => copy(lastLink)} className="rounded-full shrink-0">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="invite-list">
        {invites.length === 0 && <p className="text-sm text-muted-foreground">Nessun invito generato.</p>}
        {invites.map((inv) => {
          const s = STATUS_LABELS[inv.status] || STATUS_LABELS.pending;
          return (
            <div key={inv.id} className="flex items-center gap-3 rounded-xl border border-border p-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{inv.email}</div>
                {inv.name && <div className="text-xs text-muted-foreground truncate">{inv.name}</div>}
              </div>
              <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${s.c}`} data-testid={`invite-status-${inv.id}`}>{s.t}</span>
              {inv.status === "pending" && (
                <Button size="sm" variant="ghost" data-testid={`invite-revoke-${inv.id}`} onClick={() => revoke(inv.id)} className="rounded-full text-destructive" title="Revoca">
                  <Ban className="h-4 w-4" />
                </Button>
              )}
              {["accepted", "expired", "revoked"].includes(inv.status) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`invite-delete-${inv.id}`} className="rounded-full text-destructive" title="Elimina">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare questo invito?</AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div>
                          <span className="block">Email: <b>{inv.email}</b></span>
                          <span className="block">Stato: <b>{s.t}</b></span>
                          <span className="block mt-2">L'invito verrà rimosso dall'elenco. Eventuali account/organizzazioni già creati da questo invito <b>non</b> vengono eliminati.</span>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction data-testid={`invite-delete-confirm-${inv.id}`} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove(inv.id)}>Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
